import { Injectable, InjectionToken, Injector, OnDestroy, inject } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { PLATFORM_ADAPTER, PlatformAdapter } from "../core/platform/platform.adapter";
import { ClientTokenService } from "../services/auth/client-token.service";
import { DatabaseService } from "../services/database/database.service";
import * as paramCache from "../plugins/opsctrl-foc/param-cache";
import { SyncWsClientService } from "./sync-ws-client.service";
import { BackfillResponseMsg, Op, RemoteMsg, ServerMsg, SubscribeDoneMsg, SyncTable } from "./ws-envelope";
import { ApplyCtx, StoredRowSnapshot, classifyRemoteOp } from "./apply-classify";
import { SYNC_APPLY_TABLES, SyncTableMeta } from "../db/sync-tables";
import { buildApplyTombstone, buildApplyUpsert } from "../db/apply-remote";
import { FlightTimerService } from "../services/flight-timer/flight-timer.service";
import { CrashRecoveryService } from "../services/crash-recovery/crash-recovery.service";
import { FLUSH_MAX_CHARS, OplogRow, isTerminalReject, nextRetryDelayMs, planFlushBatch } from "../db/oplog-flush";

/** How many recent flights the BACKFILL of a full sync asks for. A trusted reconnect sends no BACKFILL. */
const BACKFILL_LIMIT = 200;

/** localStorage key: the registration whose resume point was reached by a complete catch-up. */
const RESUME_TRUSTED_KEY = "efb.syncResumeTrusted";

/** localStorage key prefix: the sync generation this device last synced under, per registration. */
const GENERATION_KEY_PREFIX = "efb.syncGeneration.";

/** Ids in one DELETE statement of the reset sweep. */
const SWEEP_DELETE_CHUNK = 200;
const SWEEP_OUTBOX_GUARD = "SELECT `rowId` FROM `sync_oplog` WHERE `tableName` = ? AND (`acked` = 0 OR `localSeq` > ?)";

/** Max ops in one APPLY frame; a larger outbox drains in successive batches. */
const FLUSH_BATCH_LIMIT = 500;

/**
 * How many of the most-recent acked op-log rows to retain locally. The server's
 * `sync_audit` is the forever archive, so an acked row is fully redundant on the
 * device — this just keeps a recent window for on-device debugging while bounding
 * the table. Unacked rows (offline edits not yet on the server, AND the local
 * version history they carry) are NEVER pruned.
 */
const OPLOG_RETENTION_KEEP = 500;

/**
 * Only probe for prunable rows after this many acked marks accrue — keeps the
 * prune SELECT off the per-drain hot path (a no-op SELECT every settled batch is
 * wasted loopback I/O). A prune also runs once on every (re)connect, so the bound
 * is still enforced promptly on reject-heavy / long-lived-connection devices.
 */
export const OPLOG_PRUNE_CHECK_INTERVAL = 100;

/**
 * Cancellable timer for the transient-reject retry tick. Injected so a test can
 * fire it deterministically without mocking the global clock (which would break
 * the spec's microtask-settle helper). Default = real setTimeout/clearTimeout.
 */
export interface RetryScheduler {
  schedule(fn: () => void, ms: number): unknown;
  cancel(handle: unknown): void;
}

export const SYNC_RETRY_SCHEDULER = new InjectionToken<RetryScheduler>("SYNC_RETRY_SCHEDULER", {
  providedIn: "root",
  factory: () => ({
    schedule: (fn, ms) => setTimeout(fn, ms) as unknown,
    cancel: (h) => {
      if (h != null) clearTimeout(h as ReturnType<typeof setTimeout>);
    },
  }),
});

/**
 * True while a sync reset must wait. A reset deletes rows, so it never runs
 * during a flight. After a restart the timer is restored only when the flow
 * briefing opens, so the crash-recovery marker (under 3 h old) counts too.
 * Resolved lazily: the timer service must not be constructed at injection time.
 */
export const SYNC_RESET_HOLD = new InjectionToken<() => boolean | Promise<boolean>>("SYNC_RESET_HOLD", {
  providedIn: "root",
  factory: () => {
    const injector = inject(Injector);
    return async () => {
      if (injector.get(FlightTimerService).isTimerRunning()) return true;
      const started = await injector.get(CrashRecoveryService).getFlightTimerStartedAt();
      return !!started?.time && Date.now() - started.time < 3 * 60 * 60 * 1000;
    };
  },
});

/** Add `value` to the set under `key`, creating the set on first use. */
function addToSetMap<K>(map: Map<K, Set<string>>, key: K, value: string): void {
  const set = map.get(key);
  if (set) set.add(value);
  else map.set(key, new Set([value]));
}

/** State of a running sync reset. */
interface ResetState {
  generation: number;
  /** Highest localSeq of the outbox when the reset started. */
  outboxMark: number;
  /** Rows with an unsent write when the reset started. Their ACK can arrive before the sweep. */
  unsentAtStart: Map<string, Set<string>>;
  /** Every row the stream held, by table. */
  seen: Map<SyncTable, Set<string>>;
}

/** Emitted AFTER a batch of remote ops has landed in local SQLite. */
export interface AppliedChange {
  table: SyncTable;
  rowIds: string[];
}

// Rows per batched snapshot read (SQLite bound-variable limit stays far above).
const SNAPSHOT_READ_CHUNK = 500;
// Ops written in one transaction.
const APPLY_WRITE_CHUNK = 200;
// Row text in one transaction. A report row can be 790 KB, so a count alone
// would put 150 MB in one request to the local server.
const APPLY_WRITE_MAX_CHARS = 1_000_000;

/** Characters of the string values of a row: a cheap size estimate. */
function rowChars(op: Op): number {
  let n = 0;
  for (const v of Object.values(op.row as Record<string, unknown>)) {
    if (typeof v === "string") n += v.length;
  }
  return n;
}

/** Split ops, in order, into chunks under both limits. A bigger op goes alone. */
function writeChunks(ops: Op[]): Op[][] {
  const chunks: Op[][] = [];
  let chunk: Op[] = [];
  let chars = 0;
  for (const op of ops) {
    const size = rowChars(op);
    if (chunk.length > 0 && (chunk.length >= APPLY_WRITE_CHUNK || chars + size > APPLY_WRITE_MAX_CHARS)) {
      chunks.push(chunk);
      chunk = [];
      chars = 0;
    }
    chunk.push(op);
    chars += size;
  }
  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

/**
 * Native peer-sync orchestrator. The web SPA re-queries the server on REMOTE
 * (TableChangeNotifierService); the iPad's store *is* local SQLite, so this
 * service writes incoming peer ops INTO that local DB — mirroring the server
 * `applyOps` guards client-side via the pure `classifyRemoteOp` (LWW /
 * tenant / missing-target) and the generic `apply-remote` SQL builder.
 *
 * Pull half of Phase 2: connects on the device clientToken. On open it sends
 * SUBSCRIBE from the resume point when that point is trusted, else BACKFILL,
 * then SUBSCRIBE from 0. It applies REMOTE/BACKFILL ops. Pushing local edits is the
 * Phase 3 durable op-log; this service does not push. Native-only — inert on
 * the browser build (web uses the re-query path).
 *
 * `applied$` fires AFTER each batch lands (not on raw REMOTE receipt), so
 * native views can reactively re-read local SQLite without racing the write.
 */
@Injectable({ providedIn: "root" })
export class NativeSyncService implements OnDestroy {
  private platform = inject<PlatformAdapter>(PLATFORM_ADAPTER);
  private syncWs = inject(SyncWsClientService);
  private clientToken = inject(ClientTokenService);
  private db = inject(DatabaseService);
  private retrySched = inject(SYNC_RETRY_SCHEDULER);
  private resetHold = inject(SYNC_RESET_HOLD);

  /** Set while a sync reset runs, from the RESET frame to its SUBSCRIBE_DONE. */
  private reset: ResetState | null = null;

  private readonly subs: Subscription[] = [];
  /** Cached local column set per table (PRAGMA table_info), so apply skips columns the legacy schema lacks. */
  private readonly localColumns = new Map<string, Set<string>>();
  /**
   * Highest serverSeq *processed* — the resume point for SUBSCRIBE on reconnect.
   * Advances for every op we act on, applied OR deliberately skipped
   * (skip-stale / skip-cross-tenant / skip-missing-target), so a
   * persistent skip-tail (e.g. our own ops, skipped as stale, or tombstones we
   * filter as missing-target) doesn't keep the watermark below the stream head
   * and force the server to re-stream that tail on every reconnect. Only live
   * ops advance it per op: see `caughtUp`. Also bumped to the authoritative
   * `maxSeq` the server reports in SUBSCRIBE_DONE. Persisted to
   * `registration.lastSeq` and seeded from it at connect so a cold start doesn't
   * replay the whole backlog from 0.
   */
  private maxSeq = 0;
  /**
   * False from the moment a connection opens until its SUBSCRIBE_DONE. Backfill
   * and catch-up batches carry one table each, so their sequence numbers are not
   * monotonic. Until the catch-up is complete no row may move the resume point:
   * an app stopped in that time would resume above rows it never applied, and
   * the server would not send them again.
   */
  private caughtUp = false;
  /**
   * The lowest serverSeq of an op we DROPPED (write failure) that has not yet
   * been re-applied. It is a hard ceiling on the watermark across batches AND on
   * the server-reported `maxSeq` adopted in SUBSCRIBE_DONE — the
   * DONE frame must not be allowed to advance the resume point past a gap, or the
   * dropped op falls below the resume point and the server never re-sends it
   * (permanent silent local divergence). Cleared once a later clean apply reaches
   * the gap (the op was redelivered and landed). `+Infinity` = no outstanding gap.
   */
  private lowestUnhealedGap = Number.POSITIVE_INFINITY;
  /** Guards seeding maxSeq from registration.lastSeq exactly once per process. */
  private seeded = false;
  /** Serializes apply batches so the read-classify-write sequence never interleaves. */
  private applyChain: Promise<void> = Promise.resolve();

  // ----- Push half (durable op-log flush, peer-sync Phase 3) -----------------
  /** True while the socket is open — gates flushing (no point sending on a closed socket). */
  private isOpen = false;
  /**
   * Ops sent in an APPLY frame and awaiting their ACK/REJECT, keyed by rowId and
   * holding the SENT localSeqs in send order (ascending). The server's per-op
   * ACK/REJECT carries only rowId, so a reply settles the *lowest sent* op for
   * that rowId — `shiftInFlight`. Keying on what was actually SENT (not the DB's
   * lowest-unacked row) is load-bearing: a later local edit to the same row
   * (a higher localSeq, not in this batch) must NOT be retired by a reply to an
   * earlier version, and a stale/duplicate reply after the queue drains is a
   * no-op instead of acking an un-sent edit. Also dedups a second flush trigger
   * (a local write mid-batch) and serves the pending-rows filter. Cleared
   * wholesale on socket drop — the next reconnect re-sends every still-unacked
   * op (idempotent: the server re-ACKs an already-applied op via same-write-retry).
   */
  private readonly inFlightByRow = new Map<string, number[]>();
  /** Serializes flush reads + ACK/REJECT marking so they never interleave. */
  private flushChain: Promise<void> = Promise.resolve();
  /**
   * Transient-reject retry tick. A transient REJECT leaves its op `acked=0` with
   * no self-retrigger (avoids a hot loop). Without a clock, an online+idle device
   * wouldn't re-push it until the next local write / reconnect — and a transient
   * on the last op of a full batch would defer the >batch tail. `retryHandle`
   * holds the single armed timer (re-arm is a no-op while one is pending);
   * `retryAttempt` drives the exponential backoff and resets to 0 on any ACK.
   */
  private retryHandle: unknown = null;
  private retryAttempt = 0;
  /** Acked marks (ACK or terminal drop) since the last prune — gates the prune SELECT. */
  private ackedSincePrune = 0;

  private resumeListener: Promise<PluginListenerHandle> | null = null;

  private readonly _applied$ = new Subject<AppliedChange>();
  readonly applied$: Observable<AppliedChange> = this._applied$.asObservable();

  constructor() {
    if (!this.platform.isNative) return;

    // Connect / disconnect as the device sync token comes and goes.
    this.subs.push(
      this.clientToken.tokenObs.subscribe((token) => {
        if (token) this.syncWs.connect(token);
        else this.syncWs.disconnect();
      })
    );

    // On every (re)connect, catch up (pull, see onOpen): SUBSCRIBE from a trusted
    // resume point, else BACKFILL recent flights, then SUBSCRIBE from 0. Also
    // flush the durable op-log outbox (push). On any
    // non-open status, abandon the in-flight set so the next reconnect re-sends.
    this.subs.push(
      this.syncWs.status$.subscribe((status) => {
        this.isOpen = status === "open";
        this.reset = null;
        if (this.isOpen) {
          this.caughtUp = false;
          this.onOpen();
          this.triggerFlush();
          // Enforce the retention bound on accumulation from a prior session, and
          // on a reject-heavy device that may never reach the per-drain interval.
          this.schedulePrune();
        } else {
          this.inFlightByRow.clear();
          // Reconnect will re-trigger the flush, so a pending backoff tick is moot.
          this.cancelRetry();
        }
      })
    );

    // A resumed app gets a new socket. The old one can look open and be dead.
    this.resumeListener = App.addListener("resume", () => this.syncWs.reconnect());

    // Apply incoming peer ops into local SQLite.
    this.subs.push(this.syncWs.messages$.subscribe((msg) => this.onMessage(msg)));

    // Real-time-online push: a local sync write while already connected flushes
    // its new op(s) within the event loop, not on the next reconnect.
    this.subs.push(this.db.oplogAppended$.subscribe(() => this.triggerFlush()));
  }

  private onOpen(): void {
    const claims = this.clientToken.getClaims();
    if (!claims) return;
    // Chain the (re)subscribe onto applyChain so a one-time watermark seed from
    // registration.lastSeq lands before SUBSCRIBE, and so an in-flight apply
    // batch can't bump maxSeq concurrently with the seed read.
    this.applyChain = this.applyChain
      .then(async () => {
        await this.seedWatermark();
        // SUBSCRIBE alone is complete, so a trusted resume point needs no BACKFILL.
        if (this.maxSeq > 0 && this.readTrusted() === claims.registrationId) {
          this.syncWs.subscribe(claims.registrationId, this.maxSeq, this.readGeneration(claims.registrationId));
        } else {
          this.requestFullSync(claims.registrationId, this.readGeneration(claims.registrationId));
        }
      })
      .catch((err) => console.error("[nativeSync] onOpen failed", err));
  }

  private readTrusted(): string | null {
    try {
      return localStorage.getItem(RESUME_TRUSTED_KEY);
    } catch {
      return null;
    }
  }

  /**
   * 0 when nothing is stored. A device that never synced under a generation
   * must still send one: with none, the server would never reset a device that
   * holds old rows from a build before the reset.
   */
  private readGeneration(registrationId: string): number {
    try {
      const n = Number(localStorage.getItem(GENERATION_KEY_PREFIX + registrationId));
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  }

  private writeGeneration(registrationId: string, generation: number): void {
    try {
      localStorage.setItem(GENERATION_KEY_PREFIX + registrationId, String(generation));
    } catch (err) {
      console.error("[nativeSync] generation write failed", err);
    }
  }

  private writeTrusted(registrationId: string | null): void {
    try {
      if (registrationId === null) localStorage.removeItem(RESUME_TRUSTED_KEY);
      else localStorage.setItem(RESUME_TRUSTED_KEY, registrationId);
    } catch (err) {
      console.error("[nativeSync] trust marker write failed", err);
    }
  }

  /**
   * Seed maxSeq from the persisted `registration.lastSeq` once per process, so a
   * cold start / app restart resumes from the last processed serverSeq instead
   * of 0. With 0, onOpen does a full sync: BACKFILL, then SUBSCRIBE from 0.
   * Best-effort: a read failure leaves maxSeq at its current value (0), so that
   * connect does a full sync.
   */
  private async seedWatermark(): Promise<void> {
    if (this.seeded) return;
    this.seeded = true;
    try {
      const rows = await this.db.selectUser(
        "SELECT `lastSeq` FROM `registration` ORDER BY (`id` = 'legacy-device') ASC, `createdAt` DESC LIMIT 1"
      );
      const persisted = Number(rows?.[0]?.lastSeq);
      if (Number.isFinite(persisted) && persisted > this.maxSeq) this.maxSeq = persisted;
    } catch (err) {
      console.error("[nativeSync] seedWatermark failed", err);
    }
  }

  private onMessage(msg: ServerMsg): void {
    if (msg.type === "ACK") {
      const rowId = msg.rowId;
      this.flushChain = this.flushChain
        .then(() => this.onAck(rowId))
        .catch((err) => console.error("[nativeSync] ack handling failed", err));
      return;
    }
    if (msg.type === "REJECT") {
      const { rowId, reason, terminal } = msg;
      this.flushChain = this.flushChain
        .then(() => this.onReject(rowId, reason, terminal))
        .catch((err) => console.error("[nativeSync] reject handling failed", err));
      return;
    }
    if (msg.type === "RESET") {
      const generation = msg.generation;
      this.applyChain = this.applyChain
        .then(() => this.onResetFrame(generation))
        .catch((err) => console.error("[nativeSync] reset failed", err));
      return;
    }
    // The catch-up follows the backfill, so its maxSeq is not a resume point yet.
    if (msg.type === "BACKFILL_DONE") return;
    if (msg.type === "SUBSCRIBE_DONE") {
      const reported = Number((msg as BackfillResponseMsg | SubscribeDoneMsg).maxSeq);
      if (reported < this.maxSeq && !this.reset) {
        // The server store was replaced. Sync everything again on this connection.
        this.applyChain = this.applyChain
          .then(() => this.resetResumePoint())
          .catch((err) => console.error("[nativeSync] resume reset failed", err));
        return;
      }
      this.caughtUp = true;
      // Adopt the server's authoritative high-water mark. This covers ops the
      // server counted in the stream but never sent us (e.g. our own ops it
      // excludes from broadcast), which the per-op watermark below can't see.
      //
      // The server emits its REMOTE batches BEFORE the DONE-with-maxSeq, so by
      // the time this frame arrives the matching REMOTE batches are already
      // QUEUED on applyChain but not yet executed. Adopting `reported`
      // synchronously here would defeat the per-op watermark cap: a REMOTE op
      // that later FAILS to apply could not pull the resume point back below its
      // seq, the dropped op would never be redelivered. So enqueue the adoption
      // onto applyChain (runs after those REMOTE applies) and clamp it to the
      // lowest unhealed gap so a dropped op still pins the resume point below it.
      if (Number.isFinite(reported)) {
        this.applyChain = this.applyChain
          .then(() => this.adoptReportedWatermark(reported))
          .catch((err) => console.error("[nativeSync] adopt watermark failed", err));
      }
      this.applyChain = this.applyChain
        .then(() => this.finishReset())
        .catch((err) => console.error("[nativeSync] reset sweep failed", err));
      return;
    }
    if (msg.type !== "REMOTE") return;
    const ops = (msg as RemoteMsg).ops;
    if (!ops || ops.length === 0) return;
    // Read the phase now, in message order: the chained apply runs later.
    const live = this.caughtUp;
    // Chain so batches apply strictly in order (the loopback read+write per op
    // must not interleave with another batch).
    this.applyChain = this.applyChain
      .then(() => this.applyBatch(ops, live))
      .catch((err) => console.error("[nativeSync] apply batch failed", err));
  }

  private async applyBatch(ops: Op[], live: boolean): Promise<void> {
    const claims = this.clientToken.getClaims();
    if (!claims) return;
    const ctx: ApplyCtx = { myRegistrationId: claims.registrationId };

    // A reset keeps every row the stream holds, whether its op is applied or skipped.
    if (this.reset) {
      for (const op of ops) {
        addToSetMap(this.reset.seen, op.table, op.rowId);
      }
    }

    const appliedByTable = new Map<SyncTable, string[]>();
    // In-batch ceiling: a dropped op (write failure) lowers it to serverSeq - 1
    // so a LATER op in the SAME batch can't advance the watermark past the gap —
    // which would make the dropped op fall below the resume point and never be
    // re-delivered (permanent local divergence). Re-delivery on the next
    // SUBSCRIBE then heals it (LWW idempotent).
    let watermarkCeiling = Number.POSITIVE_INFINITY;
    let advanced = false;
    // Set when a write failure forces the resume point DOWN below a gap whose seq
    // is at or under the already-advanced watermark (non-monotonic streams: the
    // server emits one REMOTE batch per table — flightlist before flight-scoped
    // tables in BACKFILL, REPLAY_ORDER groups in catch-up — so a higher seq can
    // land before a lower one fails). A pull-back is not an "advance", but it must
    // still be persisted, or the lowered resume point lives only in memory and a
    // cold start replays from the stale-high `registration.lastSeq`.
    let pulledBack = false;

    const snapshots = await this.readSnapshots(ops, ctx.myRegistrationId);

    const opUpdatedAt = (op: Op): number => Number((op.row as Record<string, unknown>)["updatedAt"]);
    const noteApplied = (op: Op): void => {
      const bucket = appliedByTable.get(op.table);
      if (bucket) bucket.push(op.rowId);
      else appliedByTable.set(op.table, [op.rowId]);
    };

    // Classify the chunk and write it in one transaction. Returns false when it
    // could not (missing snapshot, a statement that did not build, a failed
    // transaction): the caller then applies the chunk op by op.
    const applyChunkBatched = async (chunk: Op[]): Promise<boolean> => {
      const overlay = new Map<SyncTable, Map<string, number>>();
      const statements: { sql: string; params: unknown[] }[] = [];
      const applied = new Set<Op>();
      try {
        for (const op of chunk) {
          const meta = SYNC_APPLY_TABLES[op.table];
          if (!meta) continue;
          const own = overlay.get(op.table)?.get(op.rowId);
          const snap = own !== undefined ? own : snapshots.get(op.table)?.get(op.rowId);
          if (snap === undefined) return false;
          const stored = snap === null ? undefined : { updatedAt: snap };
          if (classifyRemoteOp(op, stored, ctx) !== "apply") continue;
          if (op.type === "upsert") {
            const localCols = await this.getLocalColumns(meta.table);
            const { sql, values } = buildApplyUpsert(op, meta, localCols, ctx.myRegistrationId);
            statements.push({ sql, params: values });
          } else {
            const { sql, values } = buildApplyTombstone(op, meta, ctx.myRegistrationId);
            statements.push({ sql, params: values });
          }
          applied.add(op);
          const updatedAt = opUpdatedAt(op);
          const t = overlay.get(op.table);
          if (t) t.set(op.rowId, updatedAt);
          else overlay.set(op.table, new Map([[op.rowId, updatedAt]]));
        }
        if (statements.length > 0) await this.db.runUserBatch(statements);
      } catch (err) {
        console.error("[nativeSync] batched write failed, applying op by op", err);
        return false;
      }
      for (const [table, rows] of overlay) {
        const snaps = snapshots.get(table);
        if (snaps) for (const [rowId, updatedAt] of rows) snaps.set(rowId, updatedAt);
      }
      for (const op of chunk) {
        const meta = SYNC_APPLY_TABLES[op.table];
        if (!meta) continue;
        const seq = Number((op.row as Record<string, unknown>)["serverSeq"]);
        const effectiveCeiling = Math.min(watermarkCeiling, this.lowestUnhealedGap);
        if (applied.has(op)) {
          this.db.seedSyncStamp(meta.table, op.rowId, opUpdatedAt(op));
          noteApplied(op);
        }
        if (live && this.bumpWatermark(seq, effectiveCeiling)) advanced = true;
        this.healGap(seq);
      }
      return true;
    };

    for (const chunk of writeChunks(ops)) {
      if (await applyChunkBatched(chunk)) continue;
      for (const op of chunk) {
        const meta = SYNC_APPLY_TABLES[op.table];
        if (!meta) continue;
        const seq = Number((op.row as Record<string, unknown>)["serverSeq"]);
        // Cross-batch ceiling: while a gap from an EARLIER batch is open, an op
        // ABOVE it (seq > gap) must not advance the watermark — that would strand
        // the gap op below the resume point. The gap op itself (seq == gap) and
        // ops below it may advance; applying the gap op is what heals the gap.
        const effectiveCeiling = Math.min(watermarkCeiling, this.lowestUnhealedGap);
        try {
          const snap = snapshots.get(op.table)?.get(op.rowId);
          // undefined: the batched read did not cover this row, so read it alone.
          const stored =
            snap === undefined
              ? await this.readSnapshot(meta, op, ctx.myRegistrationId)
              : snap === null
                ? undefined
                : { updatedAt: snap };
          if (classifyRemoteOp(op, stored, ctx) !== "apply") {
            // A deliberately-skipped op is already superseded — record its
            // seq so the skip-tail doesn't pin the watermark and force a re-stream.
            if (live && this.bumpWatermark(seq, effectiveCeiling)) advanced = true;
            this.healGap(seq);
            continue;
          }
          await this.writeOp(op, meta, ctx);
          snapshots.get(op.table)?.set(op.rowId, opUpdatedAt(op));
          if (live && this.bumpWatermark(seq, effectiveCeiling)) advanced = true;
          this.healGap(seq);
          noteApplied(op);
        } catch (err) {
          console.error(`[nativeSync] failed to apply ${op.table}/${op.rowId}`, err);
          // The op did NOT land. Cap the rest of this batch below its seq so no
          // subsequent op advances the watermark past the gap; the server re-sends
          // it on the next SUBSCRIBE (watermark < seq) and LWW re-applies it. Also
          // record it as the lowest unhealed gap so a LATER batch (or the DONE
          // watermark) can't advance the resume point past it either.
          if (Number.isFinite(seq)) {
            watermarkCeiling = Math.min(watermarkCeiling, seq - 1);
            this.lowestUnhealedGap = Math.min(this.lowestUnhealedGap, seq);
            // Non-monotonic streams can fail a LOWER seq AFTER a higher seq already
            // advanced (and persisted) the watermark. The forward ceilings above
            // only gate FUTURE advances; they can't undo an advance that already
            // happened. Pull the resume point back to below the gap so the server's
            // `serverSeq > sinceSeq` catch-up redelivers the dropped op. Without
            // this, a flightlist op (seq 100) applied before a nav op (seq 20)
            // failed would strand seq 20 below a maxSeq of 100 forever.
            if (this.maxSeq >= seq) {
              this.maxSeq = seq - 1;
              pulledBack = true;
            }
          }
        }
      }
    }

    for (const [table, rowIds] of appliedByTable) {
      // A running calculation holds the parameter scopes it read. The peer's
      // rows are stored now, so the next read must reach the database.
      if (table === "param") paramCache.clear();
      this._applied$.next({ table, rowIds });
    }
    if (advanced || pulledBack) await this.persistWatermark();
  }

  /**
   * Chained adoption of the server-reported high-water mark (SUBSCRIBE_DONE).
   * Runs on applyChain AFTER the REMOTE batches the DONE frame
   * trails, and clamps to the lowest unhealed gap so a dropped op still pins the
   * resume point below it (and is therefore redelivered on the next SUBSCRIBE).
   */
  private async adoptReportedWatermark(reported: number): Promise<void> {
    const claims = this.clientToken.getClaims();
    if (claims) this.writeTrusted(claims.registrationId);
    const target = Math.min(reported, this.lowestUnhealedGap - 1);
    if (target > this.maxSeq) {
      this.maxSeq = target;
      await this.persistWatermark();
    }
  }

  /** The server is behind the resume point: forget it and ask for every row again. */
  private async resetResumePoint(): Promise<void> {
    this.writeTrusted(null);
    this.maxSeq = 0;
    this.lowestUnhealedGap = Number.POSITIVE_INFINITY;
    await this.persistWatermark();
    const claims = this.clientToken.getClaims();
    if (claims) {
      this.requestFullSync(claims.registrationId, this.reset?.generation ?? this.readGeneration(claims.registrationId));
    }
  }

  /** BACKFILL, then SUBSCRIBE from 0 so the server replays every row once. */
  private requestFullSync(registrationId: string, generation: number | undefined): void {
    this.syncWs.backfill(registrationId, BACKFILL_LIMIT);
    this.syncWs.subscribe(registrationId, 0, generation);
  }

  /**
   * The server answered our SUBSCRIBE with RESET. While the flight timer runs we
   * subscribe as before, without a generation. Otherwise we sync again from 0.
   *
   * ponytail: a held reset is tried again only at the next connect. Add a retry
   * timer if a device stays connected for days.
   */
  private async onResetFrame(generation: number): Promise<void> {
    const claims = this.clientToken.getClaims();
    if (!claims || !this.isOpen) return;
    const registrationId = claims.registrationId;
    // Only a higher generation is an order. A server store that was lost or
    // restored answers with a lower one, and it holds fewer rows than this device.
    if (generation <= this.readGeneration(registrationId) || (await this.holdsReset())) {
      if (this.maxSeq > 0 && this.readTrusted() === registrationId) {
        this.syncWs.subscribe(registrationId, this.maxSeq);
      } else {
        this.requestFullSync(registrationId, undefined);
      }
      return;
    }
    this.writeTrusted(null);
    this.maxSeq = 0;
    this.lowestUnhealedGap = Number.POSITIVE_INFINITY;
    await this.persistWatermark();
    const rows = await this.db.selectUserOrThrow("SELECT MAX(`localSeq`) AS `m` FROM `sync_oplog`");
    const mark = Number(rows?.[0]?.m);
    const unsent = await this.db.selectUserOrThrow("SELECT `tableName`, `rowId` FROM `sync_oplog` WHERE `acked` = 0");
    const unsentAtStart = new Map<string, Set<string>>();
    for (const r of unsent ?? []) addToSetMap(unsentAtStart, String(r.tableName), String(r.rowId));
    if (!this.isOpen) return;
    this.reset = { generation, outboxMark: Number.isFinite(mark) ? mark : 0, unsentAtStart, seen: new Map() };
    this.requestFullSync(registrationId, generation);
  }

  /**
   * At the SUBSCRIBE_DONE of a reset sync: delete every local row that the stream
   * did not hold and that the outbox does not protect, then store the generation.
   * Raw SQL, so the deletes are not appended to the outbox and do not sync.
   */
  private async finishReset(): Promise<void> {
    const reset = this.reset;
    if (!reset) return;
    this.reset = null;
    const claims = this.clientToken.getClaims();
    if (!claims) return;
    if (Number.isFinite(this.lowestUnhealedGap)) {
      console.error("[nativeSync] reset sweep skipped: an op failed to apply");
      return;
    }
    try {
      // A takeoff since the reset started, or a restart in flight: no delete now.
      // No generation is stored, so the next connect resets again.
      if (await this.holdsReset()) {
        console.warn("[nativeSync] reset sweep skipped: a flight is in progress");
        return;
      }
      await this.db.whileSyncWritesPaused((expired) => this.sweep(reset, claims.registrationId, expired));
    } catch (err) {
      console.error("[nativeSync] reset sweep failed", err);
    }
  }

  /** A failed hold check holds: no delete without a clear answer. */
  private async holdsReset(): Promise<boolean> {
    try {
      return await this.resetHold();
    } catch {
      return true;
    }
  }

  /**
   * The delete checks the outbox again in its own statement. A write that
   * does not pass the pause (the crew batch of a duty sign) commits its rows
   * with their outbox entries, so the delete leaves them.
   */
  private async sweep(reset: ResetState, registrationId: string, expired: () => boolean): Promise<void> {
    const protectedRows = await this.db.selectUserOrThrow(
      "SELECT `tableName`, `rowId` FROM `sync_oplog` WHERE `acked` = 0 OR `localSeq` > ?",
      [reset.outboxMark]
    );
    const protectedIds = new Map<string, Set<string>>();
    for (const r of protectedRows ?? []) {
      addToSetMap(protectedIds, String(r.tableName), String(r.rowId));
    }
    const statements: { sql: string; params: unknown[] }[] = [];
    const lost = new Map<SyncTable, string[]>();
    for (const [key, meta] of Object.entries(SYNC_APPLY_TABLES) as [SyncTable, SyncTableMeta][]) {
      const scoped = (await this.getLocalColumns(meta.table)).has("registrationId");
      const rows = await this.db.selectUserOrThrow(
        `SELECT \`${meta.pk}\` AS \`rowId\` FROM \`${meta.table}\`${scoped ? " WHERE `registrationId` = ?" : ""}`,
        scoped ? [registrationId] : []
      );
      const seen = reset.seen.get(key);
      const keep = protectedIds.get(key);
      const unsent = reset.unsentAtStart.get(key);
      const ids = (rows ?? [])
        .map((r) => r.rowId)
        .filter((id) => id !== null && id !== undefined && id !== "")
        .map(String)
        .filter((id) => !seen?.has(id) && !keep?.has(id) && !unsent?.has(id));
      if (ids.length === 0) continue;
      lost.set(key, ids);
      for (let i = 0; i < ids.length; i += SWEEP_DELETE_CHUNK) {
        const chunk = ids.slice(i, i + SWEEP_DELETE_CHUNK);
        const marks = chunk.map(() => "?").join(", ");
        statements.push({
          sql:
            `DELETE FROM \`${meta.table}\` WHERE \`${meta.pk}\` IN (${marks})` +
            (scoped ? " AND `registrationId` = ?" : "") +
            ` AND \`${meta.pk}\` NOT IN (${SWEEP_OUTBOX_GUARD})`,
          params: [...chunk, ...(scoped ? [registrationId] : []), key, reset.outboxMark],
        });
      }
    }
    // Last look before the delete: the pause ran out, or a flight started
    // while this function read the tables. Then nothing is deleted and no
    // generation is stored, so the next connect resets again.
    // Read the pause last: the hold check awaits a file read, and a pause that
    // ran out in that time has let queued writes go on.
    // ponytail: a pause that runs out while the delete batch runs is not caught;
    // keep the gate closed until the work settles if that ever shows.
    if ((await this.holdsReset()) || expired()) {
      console.warn("[nativeSync] reset sweep stopped before its delete");
      return;
    }
    if (statements.length > 0) await this.db.runUserBatch(statements);
    this.writeGeneration(registrationId, reset.generation);
    for (const [table, rowIds] of lost) {
      if (table === "param") paramCache.clear();
      this._applied$.next({ table, rowIds });
    }
  }

  /** Advance maxSeq to `seq` if it is finite, higher, and below the batch ceiling. */
  private bumpWatermark(seq: number, ceiling: number): boolean {
    if (!Number.isFinite(seq) || seq <= this.maxSeq || seq > ceiling) return false;
    this.maxSeq = seq;
    return true;
  }

  /**
   * Clear the cross-batch gap once the EXACT previously-dropped op has been
   * successfully applied or skipped on redelivery: the hole below the watermark
   * is filled, so ops above it may advance again. Matched on `seq === gap`, not
   * `seq >= gap` — an op ABOVE the gap is blocked (effectiveCeiling pins it) and
   * must not heal it, or it would strand the still-missing gap op below the
   * advanced resume point. Called only on the success/skip paths; a re-failure of
   * the gap op re-sets the gap via the catch branch, keeping the resume point
   * pinned below it.
   */
  private healGap(seq: number): void {
    if (Number.isFinite(seq) && seq === this.lowestUnhealedGap) {
      this.lowestUnhealedGap = Number.POSITIVE_INFINITY;
    }
  }

  /**
   * Persist the watermark to `registration.lastSeq` so a reconnect / cold start
   * resumes from it instead of replaying the whole backfill. Best-effort: a
   * write failure leaves the in-memory watermark intact (next persist retries).
   * Scoped like getCurrentRegistrationId — prefers the real reg over the
   * legacy-device seed, then most-recently-created.
   */
  private async persistWatermark(): Promise<void> {
    try {
      await this.db.runUser(
        "UPDATE `registration` SET `lastSeq` = ? WHERE `id` = " +
          "(SELECT `id` FROM `registration` ORDER BY (`id` = 'legacy-device') ASC, `createdAt` DESC LIMIT 1)",
        [this.maxSeq]
      );
    } catch (err) {
      console.error("[nativeSync] persistWatermark failed", err);
    }
  }

  /**
   * Read the local updatedAt of every row of the batch, one query per table and
   * chunk. Map value: a number = local row, null = no local row, missing = the
   * read failed, so the loop falls back to readSnapshot for that op.
   */
  private async readSnapshots(
    ops: Op[],
    myRegistrationId: string
  ): Promise<Map<SyncTable, Map<string, number | null>>> {
    const idsByTable = new Map<SyncTable, Set<string>>();
    for (const op of ops) {
      if (!SYNC_APPLY_TABLES[op.table]) continue;
      const ids = idsByTable.get(op.table);
      if (ids) ids.add(op.rowId);
      else idsByTable.set(op.table, new Set([op.rowId]));
    }
    const result = new Map<SyncTable, Map<string, number | null>>();
    for (const [table, idSet] of idsByTable) {
      const meta = SYNC_APPLY_TABLES[table];
      const snaps = new Map<string, number | null>();
      result.set(table, snaps);
      const ids = [...idSet];
      for (let i = 0; i < ids.length; i += SNAPSHOT_READ_CHUNK) {
        const chunk = ids.slice(i, i + SNAPSHOT_READ_CHUNK);
        const marks = chunk.map(() => "?").join(", ");
        const scope = meta.tenantScopedPk ? " AND `registrationId` = ?" : "";
        const args = meta.tenantScopedPk ? [...chunk, myRegistrationId] : chunk;
        try {
          // selectUserOrThrow, never selectUser: see readSnapshot.
          const rows = await this.db.selectUserOrThrow(
            `SELECT \`${meta.pk}\` AS \`rowId\`, \`updatedAt\` FROM \`${meta.table}\` WHERE \`${meta.pk}\` IN (${marks})${scope}`,
            args
          );
          for (const id of chunk) snaps.set(id, null);
          for (const r of rows ?? []) snaps.set(String(r.rowId), Number(r.updatedAt));
        } catch (err) {
          console.error(`[nativeSync] batched snapshot read failed for ${table}`, err);
        }
      }
    }
    return result;
  }

  /** The local row's updatedAt (+ existence), for the LWW / missing-target guard. */
  private async readSnapshot(
    meta: SyncTableMeta,
    op: Op,
    myRegistrationId: string
  ): Promise<StoredRowSnapshot | undefined> {
    const where = meta.tenantScopedPk ? `\`${meta.pk}\` = ? AND \`registrationId\` = ?` : `\`${meta.pk}\` = ?`;
    const args = meta.tenantScopedPk ? [op.rowId, myRegistrationId] : [op.rowId];
    // Throw-on-failure read (selectUserOrThrow), NOT selectUser: this snapshot is
    // the SOLE missing-target gate and the first LWW check (classifyRemoteOp runs
    // the stale check only when stored !== undefined, and treats a non-upsert with
    // no stored row as skip-missing-target). selectUser's params path swallows every
    // query error to [], so a transient loopback read failure (DB busy / restarting)
    // would masquerade as "no local row" → undefined, and (1) a STALE upsert would be
    // classified `apply`. The write SQL still keeps a newer local row then
    // (buildApplyUpsert updates only a row with an older updatedAt). And (2) a
    // tombstone for a live row would be classified
    // skip-missing-target → the watermark advances past it → the server (which
    // re-sends only serverSeq > watermark) never redelivers it → the row stays
    // live forever. By throwing, a real failure propagates to applyBatch's per-op
    // catch (cap watermark + record gap → redeliver on next SUBSCRIBE). A
    // genuinely-absent row still returns [] → undefined correctly.
    const rows = await this.db.selectUserOrThrow(
      `SELECT \`updatedAt\` FROM \`${meta.table}\` WHERE ${where} LIMIT 1`,
      args
    );
    if (!rows || rows.length === 0) return undefined;
    return { updatedAt: Number(rows[0].updatedAt) };
  }

  private async writeOp(op: Op, meta: SyncTableMeta, ctx: ApplyCtx): Promise<void> {
    if (op.type === "upsert") {
      const localCols = await this.getLocalColumns(meta.table);
      const { sql, values } = buildApplyUpsert(op, meta, localCols, ctx.myRegistrationId);
      await this.db.runUser(sql, values);
    } else {
      const { sql, values } = buildApplyTombstone(op, meta, ctx.myRegistrationId);
      await this.db.runUser(sql, values);
    }
    // Raise the op-log stamper's floor for this row so a subsequent LOCAL edit
    // out-stamps the peer version just applied. The DB service's in-memory
    // stamper is fed only by local writes (namedCommand) and would otherwise
    // miss this remote apply — letting a same-/lower-ms local edit be
    // LWW-rejected on push (peer-sync Phase 3). Tolerates a non-numeric
    // updatedAt (seedSyncStamp ignores it).
    this.db.seedSyncStamp(meta.table, op.rowId, Number((op.row as Record<string, unknown>)["updatedAt"]));
  }

  private async getLocalColumns(table: string): Promise<Set<string>> {
    const cached = this.localColumns.get(table);
    if (cached) return cached;
    // Throw-on-failure read (selectUserOrThrow), NOT selectUser: the plugin's
    // selectUser wrapper swallows every query error and returns [], so a
    // transient PRAGMA failure (DB busy / loopback restarting) would look like
    // an empty schema. We must NOT cache that — an empty Set would make every
    // later upsert throw "missing required column(s)" in buildApplyUpsert, the
    // op would be dropped + the watermark capped, the server would re-send it,
    // and getLocalColumns would return the same poisoned empty cache → permanent
    // silent local divergence for that table until app restart. By throwing, a
    // real failure propagates to applyBatch's per-op catch (cap + redeliver);
    // by only caching a non-empty result, a transient failure self-heals on the
    // next REMOTE/reconnect re-probe.
    const rows = await this.db.selectUserOrThrow(`PRAGMA table_info(\`${table}\`)`);
    const set = new Set<string>((rows ?? []).map((r: { name: unknown }) => String(r.name)));
    if (set.size > 0) this.localColumns.set(table, set);
    return set;
  }

  // ----- Push half: durable op-log flush -------------------------------------

  /**
   * Schedule a flush onto flushChain (serialized with ACK/REJECT marking).
   * No-op while the socket is closed — the flush itself re-checks isOpen, and a
   * reconnect re-triggers. Replaces the old fire-and-forget `apply()` that
   * silently dropped local edits on a closed socket.
   */
  private triggerFlush(): void {
    if (!this.isOpen) return;
    this.flushChain = this.flushChain
      .then(() => this.flushOplog())
      .catch((err) => console.error("[nativeSync] flush failed", err));
  }

  /**
   * Drain the unacked op-log oldest-first: read up to FLUSH_BATCH_LIMIT pending
   * rows, skip any already in flight, reconstruct, drop corrupt rows, and APPLY
   * the rest. The per-op ACK/REJECT frames settle each sent op (onAck/onReject);
   * a full drain re-triggers from onAck so a large backlog flushes in successive
   * batches.
   */
  private async flushOplog(): Promise<void> {
    if (!this.isOpen) return;
    let rows: OplogRow[];
    try {
      rows = (await this.db.selectUserOrThrow(
        "SELECT `localSeq`, `tableName`, `rowId`, `op`, `rowJson` FROM `sync_oplog` " +
          "WHERE `acked` = 0 ORDER BY `localSeq` LIMIT ?",
        [FLUSH_BATCH_LIMIT]
      )) as OplogRow[];
    } catch (err) {
      console.error("[nativeSync] flush read failed", err);
      return;
    }
    const pending = rows.filter((r) => !this.isInFlight(r.rowId, Number(r.localSeq)));
    if (pending.length === 0) return;

    const batch = planFlushBatch(pending, FLUSH_BATCH_LIMIT, FLUSH_MAX_CHARS);
    // A row whose rowJson can't be reconstructed can never be sent — mark it
    // acked so it doesn't wedge the outbox head on every flush.
    for (const seq of batch.corruptSeqs) {
      console.error("[nativeSync] dropping un-reconstructable oplog row", seq);
      await this.markSeqAcked(seq);
    }
    if (batch.ops.length === 0) {
      // Corrupt-only batch: advance past the dropped prefix to the valid rows
      // behind it instead of waiting for the next external trigger.
      if (batch.corruptSeqs.length > 0) this.triggerFlush();
      return;
    }

    // Track AFTER a successful send: `apply()` returning false (socket closed
    // between the isOpen check and the send) or THROWING (serialize / send
    // error) must leave nothing tracked — the ops stay pending and re-flush on
    // the next trigger, never stranded in the in-flight map with no ACK coming.
    const sent = this.syncWs.apply(batch.ops);
    if (!sent) return;
    for (let i = 0; i < batch.ops.length; i++) {
      this.addInFlight(batch.ops[i].rowId, batch.sentSeqs[i]);
    }
  }

  /**
   * Server ACKed an op for this rowId. Settle the lowest SENT op for the rowId
   * (in-flight queue is ascending) and mark exactly that op-log row acked. A
   * reply for a rowId with no in-flight op (stale / duplicate / post-reconnect)
   * is a no-op — never retires an un-sent edit. Once the in-flight map empties,
   * re-trigger to drain any backlog beyond the last batch.
   */
  private async onAck(rowId: string): Promise<void> {
    const seq = this.shiftInFlight(rowId);
    if (seq === undefined) return;
    await this.markSeqAcked(seq);
    // Progress: a one-off transient should retry quickly, so reset the backoff.
    this.retryAttempt = 0;
    if (this.inFlightCount() === 0) await this.onDrainQuiescent();
  }

  /**
   * Server REJECTed an op for this rowId. Settle the lowest SENT op for the
   * rowId. Terminal rejects (lww / not-found / immutable / validation) drop it
   * (mark acked) — re-sending reproduces the same reject; once the in-flight map
   * empties, re-trigger to keep draining. Anything else is transient: leave
   * acked=0 so a later trigger retries it.
   *
   * Terminal-ness comes from the server's structured `terminal` flag (classified
   * at the source in `ops.ts`); `isTerminalReject` string-matching is only the
   * fallback for an older server that doesn't send the flag.
   */
  private async onReject(rowId: string, reason: string, terminal?: boolean): Promise<void> {
    const seq = this.shiftInFlight(rowId);
    if (seq === undefined) return;
    const isTerminal = typeof terminal === "boolean" ? terminal : isTerminalReject(reason);
    if (isTerminal) {
      console.warn(`[nativeSync] oplog op dropped — server reject '${reason}'`, { rowId, seq });
      await this.markSeqAcked(seq);
      // A terminal drop is forward progress too — reset the backoff like an ACK.
      this.retryAttempt = 0;
      if (this.inFlightCount() === 0) await this.onDrainQuiescent();
    } else {
      console.error(`[nativeSync] oplog op rejected '${reason}' — will retry`, { rowId, seq });
      // No self-retrigger (hot-loop guard); arm a backoff tick so an idle-online
      // device still re-pushes this op (and any >batch tail behind it).
      this.armRetry();
    }
  }

  /**
   * Arm the single backoff-gated retry timer (a no-op while one is already
   * pending). On fire it clears its handle and triggers a flush, which re-reads
   * the op-log and re-sends every still-`acked=0` row. `retryAttempt` grows the
   * delay per consecutive arm; an ACK resets it (see onAck).
   */
  private armRetry(): void {
    if (this.retryHandle !== null) return;
    const delay = nextRetryDelayMs(this.retryAttempt);
    this.retryAttempt++;
    this.retryHandle = this.retrySched.schedule(() => {
      this.retryHandle = null;
      this.triggerFlush();
    }, delay);
  }

  /** Cancel any armed retry tick and reset the backoff (socket close / destroy). */
  private cancelRetry(): void {
    if (this.retryHandle !== null) {
      this.retrySched.cancel(this.retryHandle);
      this.retryHandle = null;
    }
    this.retryAttempt = 0;
  }

  /**
   * Reached when the in-flight set empties after a settle — shared by BOTH the
   * ACK path and the terminal-reject (drop) path, so retention + draining happen
   * regardless of which reply emptied the queue. A reject-heavy device (lww /
   * immutable drops, no ACKs) must still prune. The prune is gated behind the
   * acked-mark interval so its SELECT stays off the per-drain hot path.
   */
  private async onDrainQuiescent(): Promise<void> {
    if (this.ackedSincePrune >= OPLOG_PRUNE_CHECK_INTERVAL) await this.pruneAckedOplog();
    this.triggerFlush();
  }

  /** Chain a one-off prune onto flushChain (connect-time retention enforcement). */
  private schedulePrune(): void {
    this.flushChain = this.flushChain
      .then(() => this.pruneAckedOplog())
      .catch((err) => console.error("[nativeSync] prune chain failed", err));
  }

  /**
   * Prune acked op-log rows beyond the retention window: keep the most recent
   * OPLOG_RETENTION_KEEP acked rows (recent on-device history) + every unacked
   * row (durable outbox + offline version history) forever. The watermark is the
   * oldest row still inside the window (`OFFSET KEEP-1` of the acked rows,
   * newest-first); everything acked below it is redundant (server has it in
   * `sync_audit`). Best-effort — a failure leaves the rows for the next prune.
   * Resets the since-prune counter up front so a persistent failure doesn't
   * re-probe on every drain.
   */
  private async pruneAckedOplog(): Promise<void> {
    this.ackedSincePrune = 0;
    try {
      const rows = await this.db.selectUserOrThrow(
        "SELECT `localSeq` FROM `sync_oplog` WHERE `acked` = 1 ORDER BY `localSeq` DESC LIMIT 1 OFFSET ?",
        [OPLOG_RETENTION_KEEP - 1]
      );
      const watermark = Number(rows?.[0]?.localSeq);
      if (!Number.isFinite(watermark)) return; // <= KEEP acked rows — nothing to prune
      await this.db.runUser("DELETE FROM `sync_oplog` WHERE `acked` = 1 AND `localSeq` < ?", [watermark]);
    } catch (err) {
      console.error("[nativeSync] oplog prune failed", err);
    }
  }

  /** True if `seq` is currently sent-and-unsettled for `rowId` (the pending-rows filter). */
  private isInFlight(rowId: string, seq: number): boolean {
    const arr = this.inFlightByRow.get(rowId);
    return arr !== undefined && arr.includes(seq);
  }

  /** Record a sent op (queues stay ascending — flush sends in localSeq order). */
  private addInFlight(rowId: string, seq: number): void {
    const arr = this.inFlightByRow.get(rowId);
    if (arr) arr.push(seq);
    else this.inFlightByRow.set(rowId, [seq]);
  }

  /** Remove + return the lowest sent localSeq for `rowId` (the op its next reply settles), or undefined. */
  private shiftInFlight(rowId: string): number | undefined {
    const arr = this.inFlightByRow.get(rowId);
    if (!arr || arr.length === 0) return undefined;
    const seq = arr.shift();
    if (arr.length === 0) this.inFlightByRow.delete(rowId);
    return seq;
  }

  private inFlightCount(): number {
    let n = 0;
    for (const arr of this.inFlightByRow.values()) n += arr.length;
    return n;
  }

  private async markSeqAcked(seq: number): Promise<void> {
    await this.db.runUser("UPDATE `sync_oplog` SET `acked` = 1 WHERE `localSeq` = ?", [seq]);
    this.ackedSincePrune++;
  }

  ngOnDestroy(): void {
    for (const s of this.subs) s.unsubscribe();
    void this.resumeListener?.then((h) => h.remove());
    this.cancelRetry();
  }
}
